import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { type EventLibrary } from '#app/types/analytics/eventLibrary.js';
import { areArraysEqual } from '#app/utilities/areArraysEqual.js';
import { useCallback, useRef } from 'react';

export type TrackInputs = {
  [K in InputSubmittedEventProperties['input_name']]: InputValues;
};

type InputSubmittedEventProperties = EventLibrary['input_submitted'];

type InputValues = boolean | null | number | string | string[];

export const hasChanged = <T extends Exclude<InputValues, null>>(
  firstValue: null | T | undefined,
  secondValue: null | T | undefined,
): boolean => {
  if (Array.isArray(firstValue) && Array.isArray(secondValue))
    return !areArraysEqual(firstValue, secondValue);

  return firstValue !== secondValue;
};

export const resolveValue = (value?: InputValues) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && !value) ||
    (Array.isArray(value) && value.length === 0)
  )
    return null;

  return value;
};

export const useTrackInput = <T extends Partial<TrackInputs>>(
  values: T,
  section?: number | string,
): { fixedTrack: (values: T) => void; track: () => void } => {
  const { track: analyticsTrack } = useAnalytics();
  const initialValues = useRef<T>(values);

  /**
   * @deprecated - Use the "fixedTrack" function instead.
   *
   * This function needs to be refactored away at a later point.
   */
  const track = useCallback(() => {
    const inputNames = Object.keys(values) as Array<keyof TrackInputs>;

    for (const inputName of inputNames) {
      const initialValue = resolveValue(initialValues.current[inputName]);
      const finalValue = resolveValue(values[inputName]);

      analyticsTrack('input_submitted', {
        final_value: finalValue,
        has_changed: hasChanged(finalValue, initialValue),
        initial_value: initialValue,
        input_name: inputName,
        section: section ? String(section) : undefined,
      });

      // set new initial value if the input component is not unmounted after a tracking fire
      initialValues.current[inputName] = finalValue;
    }
  }, [analyticsTrack, initialValues, section, values]);

  /**
   * This is a "fixed" version of the track function that doesn't update when "values" changes.
   * Instead we have the callback function ask for the updated values via the function argument.
   *
   * I need to discuss this with @julsgud and see if we are good with refactoring out the original.
   * -- @erictaylor
   */
  const fixedTrack = useCallback(
    (updatedValues: T) => {
      const inputNames = Object.keys(updatedValues) as Array<keyof TrackInputs>;

      for (const inputName of inputNames) {
        const initialValue = resolveValue(initialValues.current[inputName]);
        const finalValue = resolveValue(updatedValues[inputName]);

        analyticsTrack('input_submitted', {
          final_value: finalValue,
          has_changed: hasChanged(finalValue, initialValue),
          initial_value: initialValue,
          input_name: inputName,
          section: section ? String(section) : undefined,
        });

        // set new initial value if the input component is not unmounted after a tracking fire
        initialValues.current[inputName] = finalValue;
      }
    },
    [analyticsTrack, initialValues, section],
  );

  return { fixedTrack, track };
};
